/**
 * How a lane item is painted: the design-token names behind each task
 * type, and the short words a narrow bar has room for. Colours are only
 * ever the `--nx-lane-*` tokens (tokens.css, light and dark), never literals.
 *
 * A block's colour is its TYPE's, always. Schedules used to be able to
 * carry a `color` of their own, chosen from a swatch row in the
 * recurrence editor. That is gone: the picker was reachable only for
 * recurring rules (so one-off calendar entries could never be coloured,
 * and the grid was already inconsistent), and a per-schedule colour makes
 * the legend a lie. Stored colours on existing rows are no longer read
 * either, which makes old schedules match new ones with no migration --
 * and closes an unvalidated free-text string flowing into a `style`
 * attribute, since the API only ever checked `max_length=32`. The
 * `task_schedules.color` column and the v1 field are deliberately
 * untouched: nullable, defaulted, and PATCHed only when present, so
 * nothing breaks by never sending them. (This was `CATEGORY_COLOUR`'s
 * note in `calendar-categories.ts`, the first per-type palette, read by
 * nothing since the tokens and removed 2026-10-31.)
 */
import type { CSSProperties } from 'react';
import {
  CATEGORY_LABEL,
  LINEN_LABEL,
  TASK_CATEGORIES,
  type LanePaint,
} from '../calendar-categories';
import { OUTCOME_LABEL, paintOf, type LaneItem } from './lane-items';
import { formatDuration, formatRange } from './lane-time';

const TOKEN: Record<LanePaint, string> = {
  vacuum: 'vacuum',
  sweep: 'sweep',
  dust_mop: 'dust-mop',
  scrub: 'scrub',
  patrol: 'patrol',
  delivery: 'delivery',
  linen: 'linen',
  station_docking: 'station',
  dustbag: 'dustbag',
  charge: 'charge',
  custom: 'custom',
};

/** Every paint, in legend order: the task types, with Linen run after
 *  Delivery. */
export const LANE_PAINTS: readonly LanePaint[] = TASK_CATEGORIES.flatMap((c) =>
  c === 'delivery' ? (['delivery', 'linen'] as const) : [c],
);

/** Every lane token suffix, for the token guard test. */
export const LANE_TOKEN_SUFFIXES: readonly string[] = LANE_PAINTS.map((c) => TOKEN[c]);

/** What the legend and the panel call a paint. */
export function paintLabel(paint: LanePaint): string {
  return paint === 'linen' ? LINEN_LABEL : CATEGORY_LABEL[paint];
}

// Spelled out in full rather than built from TOKEN, so every `var()`
// name is greppable and the theme guard (`theme-tokens.test.ts`) can see
// that each one is defined.
const FILL: Record<LanePaint, string> = {
  vacuum: 'var(--nx-lane-vacuum)',
  sweep: 'var(--nx-lane-sweep)',
  dust_mop: 'var(--nx-lane-dust-mop)',
  scrub: 'var(--nx-lane-scrub)',
  patrol: 'var(--nx-lane-patrol)',
  delivery: 'var(--nx-lane-delivery)',
  linen: 'var(--nx-lane-linen)',
  station_docking: 'var(--nx-lane-station)',
  dustbag: 'var(--nx-lane-dustbag)',
  charge: 'var(--nx-lane-charge)',
  custom: 'var(--nx-lane-custom)',
};

const INK: Record<LanePaint, string> = {
  vacuum: 'var(--nx-lane-vacuum-ink)',
  sweep: 'var(--nx-lane-sweep-ink)',
  dust_mop: 'var(--nx-lane-dust-mop-ink)',
  scrub: 'var(--nx-lane-scrub-ink)',
  patrol: 'var(--nx-lane-patrol-ink)',
  delivery: 'var(--nx-lane-delivery-ink)',
  linen: 'var(--nx-lane-linen-ink)',
  station_docking: 'var(--nx-lane-station-ink)',
  dustbag: 'var(--nx-lane-dustbag-ink)',
  charge: 'var(--nx-lane-charge-ink)',
  custom: 'var(--nx-lane-custom-ink)',
};

export function laneColour(lane: LanePaint): string {
  return FILL[lane];
}

export function laneInk(lane: LanePaint): string {
  return INK[lane];
}

/** The custom properties a bar or swatch reads its colours from. */
export function laneVars(lane: LanePaint): CSSProperties {
  return { '--lane': FILL[lane], '--lane-ink': INK[lane] } as CSSProperties;
}

/**
 * The two-letter code a narrow bar shows, unique per type, and spelled
 * out in the legend ("Va Vacuum"). Two letters because one could not be
 * unique: Sweep, Scrub and Station all start with S.
 */
export const LANE_CODE: Record<LanePaint, string> = {
  vacuum: 'Va',
  sweep: 'Sw',
  dust_mop: 'Dm',
  scrub: 'Sc',
  patrol: 'Pa',
  delivery: 'De',
  linen: 'Ln',
  station_docking: 'St',
  dustbag: 'Rc',
  charge: 'Ch',
  custom: 'Ot',
};

/** The words a bar of `widthPx` shows: the type code, and the place (or
 *  the schedule's name) when there is room. Never empty. */
export function barText(item: LaneItem, widthPx: number): string {
  const paint = paintOf(item);
  const code = LANE_CODE[paint];
  if (item.kind === 'occurrence') return widthPx >= 80 ? item.title : code;
  if (widthPx >= 90) return item.place ? `${code} ${item.place}` : paintLabel(paint);
  return code;
}

/** What a bar says to a screen reader (and its tooltip): who, what, when,
 *  how long, how it went. */
export function itemAriaLabel(item: LaneItem, who: string, timeZone: string): string {
  const when = formatRange(item.startMs, item.endMs, timeZone);
  const length = item.endMs > item.startMs ? `, ${formatDuration(item.endMs - item.startMs)}` : '';
  const est = item.estimated ? ' (estimated)' : '';
  const high = item.high ? ', High priority' : '';
  return `${who}: ${item.title}${high}, ${when}${length}${est}, ${OUTCOME_LABEL[item.outcome]}`;
}
